/**
 * MUI Component Overrides
 *
 * Customizes default MUI component styles to match BPS brand guidelines.
 * Uses CSS variables for proper dark mode support with graceful fallbacks.
 *
 * Best Practices:
 * - Use theme.vars for CSS variables (enables runtime theme switching)
 * - Provide fallbacks for SSR and non-CSS-variable environments
 * - Use theme.applyStyles for mode-specific styling
 * - Keep overrides minimal and purposeful
 * - Maintain accessibility (contrast ratios, focus states)
 *
 * @see https://mui.com/material-ui/customization/theme-components/
 */

import type { Components, CssVarsTheme, Theme } from '@mui/material/styles';
import { alphaVar, solidVar } from './helpers/color-helpers';
import { themeTokens } from './theme-tokens';
import { BPS_BLUE, BPS_LIGHT_GREY, BPS_RED, WHITE } from './theme-colors';

const getFocusVisibleStyles = (t: CssVarsTheme) => ({
  '&&.Mui-focusVisible': {
    backgroundColor: t.vars?.palette?.action?.focus,
  },
});

/**
 * Component style overrides
 * Note: We omit "components" from Theme to avoid circular reference
 */
export const components: Components<Omit<Theme, 'components'>> = {
  // ============================================================================
  // Global Baseline
  // ============================================================================

  MuiCssBaseline: {
    styleOverrides: {
      root: {
        // Prevent unwanted text transformations
        textTransform: 'none',
      },
      // Global scrollbar styling
      // - WebKit-based browsers: use pseudo-elements
      // - Scoped to body and all descendants to catch nested scroll containers
      body: {
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          width: '12px',
        },
        '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
          backgroundColor: 'var(--mui-palette-background-default)',
        },
        // Explicitly set thumb so it doesn't inherit/red
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--mui-palette-accent-opaque)',
          borderRadius: '10px',
        },
        //
        // Firefox (optional): uncomment to set track color, second value is track
        // scrollbarColor: `${BPS_RED} ${BPS_RED}`,
      },
    },
  },

  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          backgroundColor: t.vars?.palette?.background?.appBar,
          color: t.vars?.palette?.text?.primary,
          borderBottom: `1px solid ${t.vars?.palette?.divider}`,
          transition: themeTokens.transition.default,
        };
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: '64px !important',
        padding: '0 24px !important',
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderRadius: `${themeTokens.borderRadius.small}px`,
          boxShadow: themeTokens.shadows.soft,
          backgroundColor: t.vars?.palette?.background?.paper,
          textTransform: 'none',
          transition: themeTokens.transition.default,
        };
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          maxHeight: 47,
          fontSize: 15,
          fontWeight: 700,
          textTransform: 'uppercase',
          lineHeight: '26px',
          letterSpacing: 0.46,
          padding: '8px 22px',
          borderRadius: `${themeTokens.borderRadius.small}px`,
          transition: themeTokens.transition.default,
          wordWrap: 'break-word',
          ...getFocusVisibleStyles(t),
          '&.Mui-disabled': {
            opacity: 0.38,
          },
        };
      },
      contained: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          boxShadow: themeTokens.shadows.soft,
          '&:active': {
            boxShadow: 'none',
          },
          ...theme.applyStyles('light', {
            backgroundColor: BPS_BLUE,
            color: WHITE,
            '&:hover': {
              boxShadow: themeTokens.shadows.medium,
              backgroundColor: t.vars?.palette?.primary?.dark || BPS_BLUE,
            },
          }),
          ...theme.applyStyles('dark', {
            backgroundColor: WHITE,
            color: BPS_BLUE,
            '&:hover': {
              boxShadow: themeTokens.shadows.medium,
              backgroundColor: BPS_LIGHT_GREY,
            },
          }),
        };
      },
      outlined: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.08),
          },
        };
      },
    },
    variants: [
      {
        props: { variant: 'soft' },
        style: ({ theme }) => {
          const t = theme as unknown as CssVarsTheme;
          return {
            backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.12),
            color: solidVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY),
            '&:hover': {
              backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.2),
            },
            '&:active': {
              backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.28),
            },
          };
        },
      },
      {
        props: { variant: 'nav' },
        style: ({ theme }) => {
          const t = theme as unknown as CssVarsTheme;
          return {
            textTransform: 'none',
            padding: '4px 12px',
            minWidth: 'auto',
            borderRadius: `${themeTokens.borderRadius.small}px`,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: t.vars?.palette?.action?.hover,
            },
            ...theme.applyStyles('light', {
              color: BPS_BLUE,
            }),
            ...theme.applyStyles('dark', {
              color: WHITE,
            }),
          };
        },
      },
    ],
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: `${themeTokens.borderRadius.small}px`,
        transition: themeTokens.transition.default,
      },
    },
    variants: [
      {
        props: { variant: 'soft' },
        style: ({ theme }) => {
          const t = theme as unknown as CssVarsTheme;
          return {
            backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.12),
            color: solidVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY),
            '&:hover': {
              backgroundColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_LIGHT_GREY, 0.2),
            },
          };
        },
      },
    ],
  },

  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          color: t.vars?.palette?.primary?.main,
          transition: themeTokens.transition.fast,
          '&.Mui-checked': {
            color: t.vars?.palette?.primary?.main,
          },
          '&.Mui-disabled': {
            color: t.vars?.palette?.text?.disabled,
          },
        };
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          color: t.vars?.palette?.text?.primary,
        };
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: `${themeTokens.borderRadius.small}px`,
          transition: themeTokens.transition.default,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
          },
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.applyStyles('light', {
          color: BPS_BLUE,
        }),
        ...theme.applyStyles('dark', {
          color: WHITE,
        }),
      }),
    },
  },

  MuiSlider: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.applyStyles('light', {
          color: BPS_BLUE,
        }),
        ...theme.applyStyles('dark', {
          color: WHITE,
        }),
      }),
    },
  },

  MuiToggleButton: {
    defaultProps: {
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          color: t.vars?.palette?.text?.primary,
          transition: themeTokens.transition.default,
          ...getFocusVisibleStyles(t),
          '&.Mui-selected': {
            color: t.vars?.palette?.text?.primary,
            backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.12),
            '&:hover': {
              backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.16),
            },
          },
        };
      },
    },
  },

  MuiTabs: {
    defaultProps: {
      indicatorColor: 'secondary',
      textColor: 'inherit',
    },
    styleOverrides: {
      root: {
        '& .MuiTabs-indicator': {
          backgroundColor: 'var(--mui-palette-secondary-main)', // Red underline
          height: 3, // More prominent indicator
        },
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          color: t.vars?.palette?.primary?.main,
          fontWeight: 400,
          '&.Mui-selected': {
            color: t.vars?.palette?.text?.primary,
            fontWeight: 600,
            backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.12),
          },
          '&:hover:not(.Mui-selected)': {
            color: t.vars?.palette?.text?.primary,
            backgroundColor: t.vars?.palette?.action?.hover,
          },
        };
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          color: t.vars?.palette?.primary?.main,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: '24px',
          wordWrap: 'break-word',
          textDecoration: 'underline',
          textDecorationColor: alphaVar(t, t.vars?.palette?.primary?.mainChannel, BPS_BLUE, 0.4),
          transition: themeTokens.transition.fast,
          '&:hover': {
            color: t.vars?.palette?.primary?.dark,
            textDecorationColor: t.vars?.palette?.primary?.dark,
          },
          '&:visited': {
            color: t.vars?.palette?.primary?.dark,
          },
        };
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      root: {
        '& .MuiPaper-root': {
          borderRadius: `${themeTokens.borderRadius.large}px`,
          minWidth: 296,
          maxWidth: '100%',
        },
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
          letterSpacing: '0.15px',
          borderRadius: `${themeTokens.borderRadius.small}px`,
          margin: '4px 8px',
          wordWrap: 'break-word',
          transition: themeTokens.transition.fast,
          '&:hover': {
            backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.12),
            '&:hover': {
              backgroundColor: alphaVar(t, t.vars?.palette?.secondary?.mainChannel, BPS_RED, 0.16),
            },
          },
        };
      },
    },
  },

  MuiListItemText: {
    styleOverrides: {
      root: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: 0.15,
        wordWrap: 'break-word',
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          backgroundColor: t.vars?.palette?.background?.paper,
          transition: themeTokens.transition.default,
        };
      },
      rounded: {
        borderRadius: `${themeTokens.borderRadius.medium}px`,
      },
      elevation1: {
        boxShadow: themeTokens.shadows.soft,
      },
      elevation2: {
        boxShadow: themeTokens.shadows.medium,
      },
      elevation8: {
        boxShadow: themeTokens.shadows.strong,
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: `${themeTokens.borderRadius.xlarge}px`,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          backgroundColor: t.vars?.palette?.background?.paper,
          color: t.vars?.palette?.text?.primary,
        };
      },
    },
  },

  // ============================================================================
  // Icons & Icon Buttons
  // ============================================================================

  MuiIconButton: {
    defaultProps: {
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const t = theme as unknown as CssVarsTheme;
        return {
          transition: themeTokens.transition.fast,
          ...getFocusVisibleStyles(t),
          '&:hover': {
            backgroundColor: t.vars?.palette?.action?.hover,
          },
          '&.Mui-disabled': {
            color: t.vars?.palette?.action?.disabled,
          },
          ...theme.applyStyles('light', {
            color: BPS_BLUE,
          }),
          ...theme.applyStyles('dark', {
            color: WHITE,
          }),
        };
      },
      // Contained-style IconButton (for split button patterns)
      colorPrimary: ({ theme }) => ({
        ...theme.applyStyles('light', {
          backgroundColor: BPS_BLUE,
          color: WHITE,
          '&:hover': {
            backgroundColor: theme.vars?.palette?.primary?.dark || BPS_BLUE,
          },
        }),
        ...theme.applyStyles('dark', {
          backgroundColor: WHITE,
          color: BPS_BLUE,
          '&:hover': {
            backgroundColor: BPS_LIGHT_GREY,
          },
        }),
      }),
    },
  },

  MuiListItemIcon: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          minWidth: 36,
          ...theme.applyStyles('light', {
            color: BPS_BLUE,
          }),
          ...theme.applyStyles('dark', {
            color: WHITE,
          }),
        };
      },
    },
  },

  MuiSvgIcon: {
    styleOverrides: {
      root: {
        // Icons inherit color from parent by default
        color: 'inherit',
      },
    },
  },
};
